import api from './axiosInstance';

export interface AmsAuditApproval {
  _id: string;
  amsProjectAuditId: string;
  sections: {
    auditType: SectionApproval;
    auditors: SectionApproval;
    assetsAndEquipment: SectionApproval;
  };
  checkpointApprovals: {
    projectCheckpoints: CheckpointApproval[];
    assetCheckpoints: CheckpointApproval[];
  };
  overallStatus: 'draft' | 'pending_review' | 'partially_approved' | 'approved_to_start' | 'rejected';
  submittedForReview: boolean;
  submittedAt?: string;
  submittedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  resubmissionCount: number;
  lastResubmittedAt?: string;
  auditStarted: boolean;
  auditStartedAt?: string;
  auditStartedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  department: 'AMS' | 'OMS' | 'EPC';
  createdAt: string;
  updatedAt: string;
}

export interface SectionApproval {
  status: 'not_required' | 'pending' | 'approved' | 'rejected';
  approverId?: {
    _id: string;
    name: string;
    email: string;
  };
  approverComments?: string;
  approvedAt?: string;
  rejectedAt?: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  lastModifiedBy: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface CheckpointApproval {
  checkpointId: {
    _id: string;
    name: string;
    checkpointType: 'project' | 'asset';
  };
  amsProjectAssetId?: {
    _id: string;
    name: string;
  };
  status: 'not_required' | 'pending' | 'approved' | 'rejected';
  approverId?: {
    _id: string;
    name: string;
    email: string;
  };
  approverComments?: string;
  approvedAt?: string;
  rejectedAt?: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  lastModifiedBy: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface GetAuditApprovalsParams {
  department?: 'AMS' | 'OMS' | 'EPC';
  status?: 'draft' | 'pending_review' | 'partially_approved' | 'approved_to_start' | 'rejected';
  projectId?: string;
  page?: number;
  limit?: number;
}

export interface GetAuditApprovalsResponse {
  docs: AmsAuditApproval[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage?: number;
  prevPage?: number;
}

/**
 * Get list of audit approvals with filtering
 */
export const getAuditApprovals = async (
  params: GetAuditApprovalsParams = {}
): Promise<GetAuditApprovalsResponse> => {
  const response = await api.get('/approvals/projects/audits', { params });
  return response.data.data;
};

/**
 * Get approval status for a specific audit
 */
export const getAuditApprovalById = async (auditId: string): Promise<AmsAuditApproval> => {
  const response = await api.get(`/approvals/projects/audits/${auditId}`);
  return response.data.data;
};

/**
 * Submit audit for review (requester only)
 */
export const submitAuditForReview = async (
  auditId: string,
  comments?: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(`/approvals/projects/audits/${auditId}/submit`, {
    comments,
  });
  return response.data.data;
};

/**
 * Resubmit audit after rejection (requester only)
 */
export const resubmitAudit = async (auditId: string): Promise<AmsAuditApproval> => {
  const response = await api.post(`/approvals/projects/audits/${auditId}/resubmit`);
  return response.data.data;
};

/**
 * Approve a section (approver only)
 */
export const approveSection = async (
  auditId: string,
  sectionName: 'auditType' | 'auditors' | 'assetsAndEquipment',
  comments?: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(
    `/approvals/projects/audits/${auditId}/sections/${sectionName}/approve`,
    { comments }
  );
  return response.data.data;
};

/**
 * Reject a section with comments (approver only)
 */
export const rejectSection = async (
  auditId: string,
  sectionName: 'auditType' | 'auditors' | 'assetsAndEquipment',
  comments: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(
    `/approvals/projects/audits/${auditId}/sections/${sectionName}/reject`,
    { comments }
  );
  return response.data.data;
};

/**
 * Approve a checkpoint (approver only)
 */
export const approveCheckpoint = async (
  auditId: string,
  checkpointId: string,
  comments?: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(
    `/approvals/projects/audits/${auditId}/checkpoints/${checkpointId}/approve`,
    { comments }
  );
  return response.data.data;
};

/**
 * Reject a checkpoint with comments (approver only)
 */
export const rejectCheckpoint = async (
  auditId: string,
  checkpointId: string,
  comments: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(
    `/approvals/projects/audits/${auditId}/checkpoints/${checkpointId}/reject`,
    { comments }
  );
  return response.data.data;
};

/**
 * Approve multiple checkpoints in one request (approver only)
 */
export const bulkApproveCheckpoints = async (
  auditId: string,
  checkpointIds: string[],
  comments?: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(
    `/approvals/projects/audits/${auditId}/checkpoints/bulk-approve`,
    { checkpointIds, comments }
  );
  return response.data.data;
};

/**
 * Reject multiple checkpoints in one request with a single shared comment (approver only)
 */
export const bulkRejectCheckpoints = async (
  auditId: string,
  checkpointIds: string[],
  comments: string
): Promise<AmsAuditApproval> => {
  const response = await api.post(
    `/approvals/projects/audits/${auditId}/checkpoints/bulk-reject`,
    { checkpointIds, comments }
  );
  return response.data.data;
};

/**
 * Start the approved audit
 */
export const startAudit = async (auditId: string): Promise<AmsAuditApproval> => {
  const response = await api.post(`/approvals/projects/audits/${auditId}/start`);
  return response.data.data;
};

export interface BulkSubmitAuditsResult {
  submitted: number;
  skipped: Array<{ auditId: string; reason: string }>;
  results: Array<{
    auditId: string;
    status: 'submitted' | 'resubmitted' | 'skipped';
    overallStatus?: string;
    reason?: string;
  }>;
}

/**
 * Submit multiple audits for review in one request (requester only).
 */
export const bulkSubmitAudits = async (
  auditIds: string[],
  comments?: string
): Promise<BulkSubmitAuditsResult> => {
  const response = await api.post('/approvals/projects/audits/bulk-submit', {
    auditIds,
    comments,
  });
  return response.data.data;
};
